/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@portal/environments/environment';
import { compact } from 'lodash-es';
import { MenuItem } from 'primeng/api';

// Services
import { AuthService } from '@portal/app/shared/services/auth.service';
import { FeatureConfigService } from '@portal/app/shared/services/feature-config.service';
import { SelectionService } from './selection.service';

// Types
import { FeatureConfigResponseDTO } from '@libs/api';
import { UserModel } from '@portal/app/shared/models/userModel';
import type {
  Filter,
  FilterValue,
  IRoutedFilterFromHomePage
} from '@portal/app/shared/types';
import { NavbarConstants } from '@portal/app/shared/types/navigation';
import { UserType } from '../models/userModel';
import {
  ConfigDashboardInstance,
  CurrentStatus,
  DisplayDashboard
} from '../types';

// Constants
import { FILTER_LITERAL_IDS } from '@portal/app/dashboard/home-page/home-page.constants';
import { DashboardLiteralIds, FilterIds } from '@portal/app/shared/constants';
import { ConfigDashboardService } from './config-dashboard.service';

const {
  conversionTypeLiteralId,
  dateStartLiteralId,
  compareDateLiteralId,
  dateStopLiteralId,
  relativeDayLiteralId,
  previousDateStartLiteralId,
  previousDateStopLiteralId
} = FILTER_LITERAL_IDS;

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  constructor(
    private readonly authService: AuthService,
    private readonly selectionService: SelectionService,
    private readonly router: Router,
    private readonly configDashboardService: ConfigDashboardService,
    private readonly featureConfigService: FeatureConfigService
  ) {}

  private getControlTowerMenuItem(): MenuItem[] {
    const user = this.authService.getUser() as UserModel | null;
    if (user?.getUserType() === 'internal') {
      return [
        {
          id: NavbarConstants.controlTowerId,
          label: 'Control Tower',
          routerLink: `/a/${this.selectionService.buildSelectionSlug(
            this.selectionService.getSelection()
          )}/products/control-tower`
        }
      ];
    }
    return [];
  }

  private isConfigDashboardVisible(
    configDashboards: Record<string, ConfigDashboardInstance>,
    literalId = ''
  ): boolean {
    return this.configDashboardService.isConfigDashboardVisible(
      configDashboards,
      literalId
    );
  }

  private buildOnboardingURL() {
    if (environment.innovation) {
      return `${window.location.protocol}//${window.location.hostname}:4300`;
    }
    return 'https://onboarding.measured.com';
  }

  createUserOptionsMenuItems(
    configDashboards: Record<string, ConfigDashboardInstance>,
    updateLogOutModalIsVisible: (value: boolean) => void
  ): MenuItem[] {
    return [
      {
        id: NavbarConstants.userAccountId,
        routerLink: '/a/user',
        label: 'My Profile'
      },
      {
        id: 'logout',
        command: (): void => {
          updateLogOutModalIsVisible(true);
          this.authService.logout();
        },
        label: 'Log Out'
      }
    ];
  }

  createNativeExportReportMenu(
    configDashboards: Record<string, ConfigDashboardInstance>,
    brandProductPath: string
  ): MenuItem | null {
    const user = this.authService.getUser() as UserModel | null;
    const exportDataDashboard = configDashboards['export-data'];
    const userType = user?.getUserType();
    if (
      exportDataDashboard &&
      (userType === UserType.internal ||
        (userType === UserType.external &&
          exportDataDashboard.status === 'ACTIVE'))
    ) {
      const label =
        exportDataDashboard.displayName ||
        exportDataDashboard.configDashboard.displayName;

      return {
        routerLink: `/a/${brandProductPath}/export-report`,
        label: `${label}${
          exportDataDashboard.status === 'DRAFT' ? ' (BETA)' : ''
        }`
      };
    }
    return null;
  }

  createSettingsMenuItems(
    configDashboards: Record<string, ConfigDashboardInstance>,
    brandProductPath: string
  ): MenuItem[] {
    const dataExportMenuItem = this.createNativeExportReportMenu(
      configDashboards,
      brandProductPath
    );

    const menuItems: MenuItem[] = [];
    const selectionSlug = this.selectionService.buildSelectionSlug(
      this.selectionService.getSelection()
    );

    if (
      this.isConfigDashboardVisible(
        configDashboards,
        NavbarConstants.ddiSettingsId
      )
    ) {
      const instance = configDashboards[NavbarConstants.ddiSettingsId];
      menuItems.push({
        id: NavbarConstants.ddiSettingsId,
        label: this.getConfigDashboardLabel(instance, 'Settings Manager'),
        url: `a/${selectionSlug}/products/settings-manager/settings`,
        title: 'Settings Manager',
        routerLink: `/a/${selectionSlug}/products/settings-manager/settings`
      });
    }

    if (dataExportMenuItem) {
      menuItems.push({
        ...dataExportMenuItem,
        label: dataExportMenuItem.label || 'Data Export',
        routerLink: `/a/${selectionSlug}/products/export-report`
      });
    }

    if (
      this.isConfigDashboardVisible(
        configDashboards,
        NavbarConstants.adminPageId
      )
    ) {
      const instance = configDashboards[NavbarConstants.adminPageId];
      menuItems.push({
        id: NavbarConstants.adminPageId,
        label: this.getConfigDashboardLabel(instance, 'Administration'),
        routerLink: `/a/${selectionSlug}/products/admin`
      });
    }

    if (
      this.isConfigDashboardVisible(
        configDashboards,
        NavbarConstants.campaignMapping
      )
    ) {
      const instance = configDashboards[NavbarConstants.campaignMapping];
      const baseLabel = instance?.displayName || 'Campaign Mapping';
      const sparklesHtml =
        this.authService.getUser()?.getUserType() === UserType.internal
          ? '<img height="16px" width="16px" class="ml-2" src="assets/images/sparkles.svg" alt="Sparkles" />'
          : '';
      menuItems.push({
        id: NavbarConstants.campaignMapping,
        label: `<span>${baseLabel}</span>${sparklesHtml}`,
        escape: false,
        routerLink: `/a/${selectionSlug}/products/campaign-mapping`
      });
    }

    this.addOnboardingMenuItem(configDashboards, menuItems);

    menuItems.push(...this.getControlTowerMenuItem());

    return menuItems;
  }

  private getConfigDashboardLabel(
    instance: ConfigDashboardInstance | undefined,
    fallback: string,
    appendBeta = false
  ): string {
    const label =
      instance?.displayName ||
      instance?.configDashboard?.displayName ||
      fallback;
    const labelWithBeta = appendBeta ? `${label} (BETA)` : label;
    return instance?.status === CurrentStatus.draft ? labelWithBeta : label;
  }

  private addOnboardingMenuItem(
    configDashboards: Record<string, ConfigDashboardInstance>,
    menuItems: MenuItem[]
  ): void {
    const user = this.authService.getUser() as UserModel | null;
    const isInternal = user?.getUserType() === UserType.internal;
    if (
      this.isConfigDashboardVisible(
        configDashboards,
        NavbarConstants.onboardingId
      ) &&
      isInternal
    ) {
      const instance = configDashboards[NavbarConstants.onboardingId];
      menuItems.push({
        id: NavbarConstants.onboardingId,
        label: this.getConfigDashboardLabel(instance, 'Onboarding'),
        url: this.buildOnboardingURL(),
        title: 'Client Onboarding',
        target: '_blank'
      });
    }
  }

  getBenchmarksLink(
    dashboards: DisplayDashboard[],
    brandProductPath: string
  ): string | undefined {
    const benchmarkDashboard = dashboards.find(
      (d) =>
        d.literalId === NavbarConstants.benchmarksId &&
        d.status !== CurrentStatus.inactive
    );
    return benchmarkDashboard
      ? `/a/${brandProductPath}/${benchmarkDashboard.routerLinkPart}`
      : undefined;
  }

  // Returns product-root match paths for any active MPO/MPO v2 dashboards so the
  // Optimize tab stays highlighted on deep sub-routes (e.g. /products/mpo/plans/1).
  getOptimizeMatchPaths(
    dashboards: DisplayDashboard[],
    brandProductPath: string
  ): string[] {
    const productIds = new Set<string>();
    for (const d of dashboards) {
      if (d.status === CurrentStatus.inactive) {
        continue;
      }
      if (
        d.productId === NavbarConstants.mpoProductId ||
        d.productId === NavbarConstants.mpoV2ProductId
      ) {
        productIds.add(d.productId);
      }
    }
    return [...productIds].map(
      (productId) => `/a/${brandProductPath}/${productId}`
    );
  }

  // The new Geo Tests experience (geo-designer-v2) and the legacy v1
  // geo-designer view are both gated by the "Geo Tests" config dashboard
  // (literalId 'geo-designer'): ACTIVE shows it to everyone, DRAFT to internal
  // users only, INACTIVE/missing hides it entirely.
  private isGeoTestsV2Available(
    configDashboards: Record<string, ConfigDashboardInstance>
  ): boolean {
    return this.isConfigDashboardVisible(
      configDashboards,
      NavbarConstants.geoDesignerId
    );
  }

  getGeoTestsV2Link(
    configDashboards: Record<string, ConfigDashboardInstance>,
    brandProductPath: string
  ): string | undefined {
    return this.isGeoTestsV2Available(configDashboards)
      ? `/a/${brandProductPath}/geo-designer-v2/home`
      : undefined;
  }

  // MIM (Measured Incrementality Model) is gated by the same config-dashboard
  // visibility rules as the other nav links: ACTIVE shows to everyone, DRAFT to
  // internal users only, INACTIVE/missing hides it entirely.
  getMimPageLink(
    configDashboards: Record<string, ConfigDashboardInstance>,
    brandProductPath: string
  ): string | undefined {
    return this.isConfigDashboardVisible(
      configDashboards,
      NavbarConstants.mimPageId
    )
      ? `/a/${brandProductPath}/mim-page`
      : undefined;
  }

  isNotificationCenterAvailable(
    configDashboards: Record<string, ConfigDashboardInstance>
  ): boolean {
    return this.isConfigDashboardVisible(
      configDashboards,
      NavbarConstants.notificationCenterId
    );
  }

  getCrossChannelLink(
    dashboards: DisplayDashboard[],
    brandProductPath: string
  ): string | undefined {
    const crossChannel = dashboards.find(
      (d) =>
        d.literalId === NavbarConstants.crossChannelId &&
        d.status !== CurrentStatus.inactive
    );
    return crossChannel
      ? `/a/${brandProductPath}/${crossChannel.routerLinkPart}`
      : undefined;
  }

  createExperimentMenuItems(
    dashboards: DisplayDashboard[],
    configDashboards: Record<string, ConfigDashboardInstance>,
    brandProductPath: string
  ): MenuItem[] {
    const filteredDashboards = dashboards
      .filter((d) => d.status !== CurrentStatus.inactive)
      .filter(
        (d) =>
          d.dashboardId === NavbarConstants.experimentDashboardId ||
          NavbarConstants.experimentIds.includes(d.literalId) ||
          d.label.match(NavbarConstants.experimentLabelRegex) != null
      )
      .filter((d) => {
        if (d.dashboardId === NavbarConstants.experimentDashboardId) {
          return this.isConfigDashboardVisible(configDashboards, d.literalId);
        }
        return true;
      });

    if (!this.isGeoTestsV2Available(configDashboards)) {
      return filteredDashboards.map((d) =>
        this.toExperimentMenuItem(d, configDashboards, brandProductPath)
      );
    }

    // When Geo Tests v2 is active, it becomes the top-level "Experiment" link
    // and is removed from this list. The v1 Geo Designer entry is also hidden
    // (redundant with v2); legacy geo-doe is surfaced as "Geo Tests 1.5 (Legacy)".
    const legacyDashboard = filteredDashboards.find(
      (d) => d.literalId === NavbarConstants.geoDoeId
    );
    const otherDashboards = filteredDashboards.filter(
      (d) =>
        d.literalId !== NavbarConstants.geoDesignerId &&
        d.literalId !== NavbarConstants.geoDoeId
    );

    const menuItems: MenuItem[] = otherDashboards.map((d) =>
      this.toExperimentMenuItem(d, configDashboards, brandProductPath)
    );

    if (legacyDashboard) {
      menuItems.push({
        id: NavbarConstants.geoDoeId,
        label: 'Geo Tests 1.5 (Legacy)',
        routerLink: `/a/${brandProductPath}/${legacyDashboard.routerLinkPart}`
      });
    }

    return menuItems;
  }

  createMoreMenuItems(
    dashboards: DisplayDashboard[],
    configDashboards: Record<string, ConfigDashboardInstance>,
    brandProductPath: string
  ): MenuItem[] {
    return [
      ...this.createAnalyzeMenuItems(dashboards, brandProductPath),
      ...this.createExperimentMenuItems(
        dashboards,
        configDashboards,
        brandProductPath
      )
    ];
  }

  private toExperimentMenuItem(
    d: DisplayDashboard,
    configDashboards: Record<string, ConfigDashboardInstance>,
    brandProductPath: string
  ): MenuItem {
    const configInstance = configDashboards[d.literalId];
    const label =
      configInstance?.displayName ||
      configInstance?.configDashboard?.displayName ||
      d.label;
    return {
      routerLink: `/a/${brandProductPath}/${d.routerLinkPart}`,
      label
    };
  }

  /**
   * Creates an array of MenuItem objects based on the provided dashboards and brand product path.
   * If an optimization report exists, it is inserted between the first and remaining dashboard items.
   * Optionally, a Media Plan Optimizer v2 (Beta) link is also added if the feature flag is enabled.
   *
   * @param dashboards - An array of dashboard objects, each containing details about individual dashboards.
   * @param brandProductPath - The base path for brand-specific products used in generating URLs for the menu items.
   * @returns An array of menu items ready for use in a navigation component.
   */
  createOptimizeMenuItems(
    dashboards: DisplayDashboard[],
    brandProductPath: string
  ): MenuItem[] {
    return dashboards
      .filter(
        (d) =>
          (d.productId === NavbarConstants.mpoProductId ||
            d.productId === NavbarConstants.mpoV2ProductId) &&
          d.status !== CurrentStatus.inactive
      )
      .map((d) => ({
        label: d.label,
        routerLink: `/a/${brandProductPath}/${d.routerLinkPart}`,
        queryParams: d.isMultiView ? { isMultiView: true } : undefined
      }));
  }

  createAnalyzeMenuItems(
    dashboards: DisplayDashboard[],
    brandProductPath: string
  ): MenuItem[] {
    return dashboards
      .filter(
        (d) =>
          d.literalId !== NavbarConstants.crossChannelId &&
          d.literalId !== NavbarConstants.ddiReportingRevenue &&
          d.literalId !== NavbarConstants.ddiReportingConversion &&
          d.literalId !== NavbarConstants.homePageId &&
          d.literalId !== NavbarConstants.benchmarksId &&
          d.literalId !== NavbarConstants.notificationCenterId &&
          d.literalId !== DashboardLiteralIds.inProgress &&
          !NavbarConstants.experimentIds.includes(d.literalId) &&
          d.label.match(NavbarConstants.experimentLabelRegex) == null &&
          d.productId !== NavbarConstants.mpoProductId &&
          d.productId !== NavbarConstants.mpoV2ProductId &&
          d.status !== CurrentStatus.inactive
      )
      .flatMap((d) => {
        const items = [];

        // Handle Shopify Dashboard separately
        if (d.literalId === NavbarConstants.shopifyDashboardId) {
          items.push({
            routerLink: `/a/${brandProductPath}/shopify-dashboard`,
            label: `Shopify Dashboard ${d.status === 'DRAFT' ? ' (BETA)' : ''}`
          });
        }

        // Default menu item
        if (items.length === 0) {
          items.push({
            routerLink: `/a/${brandProductPath}/${d.routerLinkPart}`,
            label: `${d.label}${d.status === 'DRAFT' ? ' (BETA)' : ''}`
          });
        }

        return items;
      });
  }

  /**
   * Navigates to a product-related route with given additional path segments and query parameters.
   *
   * @param additionalSegments Additional segments to append to the base URL path.
   * @param queryParams Query parameters for the navigation.
   * @param state State parameters for the navigation.
   * @param newTab Whether to open the navigation in a new tab. Defaults to false.
   */
  navigateToProduct(
    additionalSegments: string[],
    queryParams: Record<string, any>,
    state: Record<string, any>,
    newTab = false
  ): void {
    const brandSlug = this.selectionService.buildSelectionSlug();
    const baseSegments = ['a', brandSlug, 'products'];
    const fullSegments = baseSegments.concat(compact(additionalSegments));
    const url = this.router
      .createUrlTree(fullSegments, { queryParams })
      .toString();

    if (newTab) {
      window.open(url, '_blank');
    } else {
      this.router
        .navigate(fullSegments, { queryParams, state })
        .catch((err) => {
          console.error('Navigation error:', err);
        });
    }
  }

  /**
   * Maps a type to a specific navigation route and parameters.
   *
   * @param type The type of navigation.
   * @param params Parameters for the navigation.
   * @param newTab Whether to open the navigation in a new tab. Defaults to false.
   */
  navigationMapping(
    type:
      | 'exploreChannel'
      | 'spendAllocation'
      | 'mpo'
      | 'mpoV2'
      | 'benchmarksDrillDown'
      | 'benchmarks'
      | 'geo'
      | 'optimization-report'
      | 'geoDesignerV2',
    params: { filters: Filter[]; extraParams?: Record<string, any> },
    newTab = false
  ) {
    let productId = '';
    let dashboardId = '';
    let queryParams = {};
    const stateObj: IRoutedFilterFromHomePage = {
      from: 'home-page',
      filters: []
    };

    switch (type) {
      case 'exploreChannel': {
        productId = 'portfolio';
        dashboardId = 'cross-channel';
        stateObj.filters = this.prepareXCDState(params);
        break;
      }
      case 'spendAllocation': {
        productId = 'portfolio';
        dashboardId = 'cross-channel';
        stateObj.filters = this.prepareXCDState(params);
        break;
      }
      case 'mpo': {
        productId = 'media-plan-optimizer';
        dashboardId = 'portfolio-level';
        const paramObj = this.prepareMpoQueryParamAndState(params);
        queryParams = paramObj.queryParams;
        stateObj.filters = paramObj.state as {
          literalId: string;
          value: FilterValue;
        }[];
        break;
      }
      case 'mpoV2': {
        productId = 'mpo';
        const paramObj = this.prepareMpoQueryParamAndState(params);
        queryParams = paramObj.queryParams;
        stateObj.filters = paramObj.state as {
          literalId: string;
          value: FilterValue;
        }[];
        break;
      }
      case 'benchmarksDrillDown': {
        productId = 'measured-benchmarks';
        dashboardId = 'benchmarks-drill-down';
        queryParams = params.extraParams?.queryParams || {};
        break;
      }
      case 'benchmarks': {
        productId = 'measured-benchmarks';
        dashboardId = 'measured-benchmarks';
        queryParams = params.extraParams?.queryParams || {};
        break;
      }
      case 'geo': {
        productId = 'geo-designer';
        dashboardId = 'home';
        stateObj.conversionTypeId = params.extraParams?.conversionTypeId;
        queryParams = params.extraParams?.queryParams || {};
        break;
      }
      case 'geoDesignerV2': {
        productId = 'geo-designer-v2';
        dashboardId = 'home';
        stateObj.conversionTypeId = params.extraParams?.conversionTypeId;
        queryParams = params.extraParams?.queryParams || {};
        break;
      }
      case 'optimization-report': {
        dashboardId = 'optimization-report';
        queryParams = params.extraParams?.queryParams || {};
        break;
      }
      default:
        break;
    }

    this.navigateToProduct(
      [productId, dashboardId],
      queryParams,
      stateObj,
      newTab
    );
  }

  private prepareCommonNavigationParams(
    filters: Filter[]
  ): Record<string, any> {
    const conversionTypeFilter = filters.find(
      (f) => f.literalId === conversionTypeLiteralId
    );
    const conversionTypeValue = conversionTypeFilter?.value;
    // Multi-select when filter type is string[], or when multi-CT-by-attributes
    // is enabled (mapped attribute picks expand to multiple conversion types).
    const multiCtByAttributesEnabled =
      this.featureConfigService.isFeatureEnabled(
        FeatureConfigResponseDTO.FeatureNameEnum.MultiCtByAttributes
      );
    const isMultiSelectConversionType =
      conversionTypeFilter?.type === 'string[]' || multiCtByAttributesEnabled;
    return {
      compare: true,
      day: [
        filters.find((f) => f.literalId === dateStartLiteralId)
          ?.value as string,
        filters.find((f) => f.literalId === dateStopLiteralId)?.value as string
      ],
      [relativeDayLiteralId as string]: filters.find(
        (f) => f.literalId === relativeDayLiteralId
      )?.value as string,
      [conversionTypeLiteralId as any]: isMultiSelectConversionType
        ? Array.isArray(conversionTypeValue)
          ? conversionTypeValue
          : [conversionTypeValue]
        : conversionTypeValue,
      [compareDateLiteralId as string]: [
        filters.find((f) => f.literalId === previousDateStartLiteralId)
          ?.value as string,
        filters.find((f) => f.literalId === previousDateStopLiteralId)
          ?.value as string
      ]
    };
  }

  private transformNavigationStateObj(navigationDataObj: Record<string, any>) {
    return Object.keys(navigationDataObj).map((key) => {
      return {
        literalId: key,
        value: navigationDataObj[key]
      };
    });
  }

  private prepareXCDState(params: {
    filters: Filter[];
    extraParams?: Record<string, any>;
  }) {
    const { filters, extraParams } = params;
    // Homepage CT is single/rollup — clear attribute picks so XCD UI matches
    // the conversion_type passed from homepage instead of stale mapped state.
    const navigationDataObj: Record<string, any> = {
      ...this.prepareCommonNavigationParams(filters),
      [FilterIds.conversionTypeAttributes]: null,
      channel: extraParams && extraParams.channel ? extraParams.channel : []
    };
    return this.transformNavigationStateObj(navigationDataObj);
  }

  private prepareMpoQueryParamAndState(params: {
    filters: Filter[];
    extraParams?: Record<string, any>;
  }) {
    const { filters, extraParams } = params;
    return {
      queryParams: {
        isMultiView: true
      },
      state: [
        {
          literalId: 'optimizeStrengthFilter',
          value:
            extraParams && extraParams.optimizeStrengthFilterValue
              ? extraParams.optimizeStrengthFilterValue
              : 1
        },
        {
          literalId: relativeDayLiteralId,
          value: filters.find((f) => f.literalId === relativeDayLiteralId)
            ?.value as string
        },
        {
          literalId: conversionTypeLiteralId,
          value: filters.find((f) => f.literalId === conversionTypeLiteralId)
            ?.value as string
        }
      ]
    };
  }
}
